/**
 * Configento Compat-Layer — bridges legacy storefront theme code onto the
 * new `window.Configento` surface. Optional bundle, loaded BEFORE
 * `configento.js` (both with `defer` so order is deterministic):
 *
 *   <script src="https://cloud.configento.app/configento-v3/configento-compat.js" defer></script>
 *   <script src="https://cloud.configento.app/configento-v3/configento.js" defer></script>
 *
 * Provides three bridges:
 *
 *  1. **DOM**: if a legacy `#configurator1` element exists without the
 *     `data-configento` attribute, the compat layer augments it in-place
 *     so the V3 bundle picks it up. Optional `data-component-id` is
 *     filled from `window.CONFIGENTO_PRODUCT.getTemplateId()` when not
 *     already set.
 *
 *  2. **Events**: subscribes to each instance's `change` event after the
 *     bundle is ready and dispatches the legacy custom events
 *     (`configentoUpdateOptionsInDomBefore` / `…After`,
 *     `configentoUpdateHeaderInDomBefore` / `…After`) on `document`.
 *     `…After` fires once the change is rendered, after every step change
 *     and once after the first render.
 *     Old theme code listening for these via `document.addEventListener`
 *     or jQuery keeps working.
 *
 *  3. **Price callback**: forwards `price` events to the legacy
 *     `CONFIGENTO_PRODUCT.setPrice()` hook when the host defines it. The
 *     legacy callback was the canonical "host shows the total" hook —
 *     themes expect to receive the configurator's running total there.
 *
 * Failure mode: if `window.CONFIGENTO_PRODUCT` doesn't exist and there's
 * no `#configurator1` mount target, this script is effectively a no-op
 * (no globals leak, no DOM mutations). Customers who never touched
 * legacy theme code can ignore the bundle entirely.
 */

import type { ConfiguratorPublicApi, ConfiguratorInstance } from './public-api';

interface LegacyProduct {
    getTemplateId?(): string | number | null;
    setPrice?(price: number): void;
    [key: string]: unknown;
}

/** Legacy global — `Justselling_Configento_App_Helper`. Storefront themes
 *  built against the original AngularJS bundle read shop context (template
 *  id, customer id, backend URL) through this namespace. The compat layer
 *  installs a thin shim that forwards onto the V3 surface so unchanged
 *  theme code keeps working. */
interface LegacyHelperShim {
    getConfigurator?(): ConfiguratorInstance | null;
    getTemplateId?(): string | number | null;
    getCustomerId?(): string | null;
    getBackendUrl?(): string | null;
    [key: string]: unknown;
}

/** Legacy global — `Justselling_Configento_App_CloudAsset`. The original
 *  `storeCloudAsset(value, type, cb, filename)` posted to `storeasset.php`
 *  and called the legacy success callback `(id, folder, type, file)`. The
 *  compat shim translates that onto the V3 `inst.storeAsset()` so existing
 *  saved-configuration / share-URL code keeps working. */
interface LegacyCloudAssetShim {
    storeCloudAsset?(
        value: unknown,
        type: string | undefined,
        cb?: (id?: string | null, folder?: string | null, type?: string | null, file?: string | null) => void,
        filename?: string,
    ): void;
    getAssets?(): unknown[];
    [key: string]: unknown;
}

/** Legacy global — `Justselling_Configento_App_Wizard`. The AngularJS
 *  bundle owned per-group tab navigation + form validation; V3 handles
 *  both inside its React tree, so most methods become no-ops. We surface
 *  `validateForm` (used by Magento's product.js in the AddToWishlist and
 *  AddToCart paths) as a permissive true — real validation happens on
 *  save-button submit inside the bundle. Other methods are shimmed as
 *  no-ops so unchanged legacy theme code doesn't hit ReferenceError. */
interface LegacyWizardShim {
    validateForm?(): boolean;
    checkValidationForActiveGroup?(): boolean;
    checkValidationForAllGroups?(): boolean;
    setActivateGroup?(index: number): void;
    setDisableGroup?(index: number): void;
    setEnableGroup?(index: number): void;
    updateWizard?(): void;
    popUpMsg?(): void;
    lastTabId?: number;
    init?: boolean;
    [key: string]: unknown;
}

/** Legacy global — `Justselling_Configento_App_Webshop`. Held the
 *  "finalize then original add-to-cart" flow: run rule validation,
 *  push assets to the cloud, then invoke the shop's original submit.
 *  V3's `inst.finalize()` produces the same envelope in one async
 *  call; the compat shim awaits it, then invokes the legacy callback
 *  synchronously with the stored params. */
interface LegacyWebshopShim {
    finalizeAction?(doneCallback: ((params?: unknown) => void) | null, doneParams?: unknown): boolean | void;
    finalizeActionDone?(): void;
    getCallback?(): unknown;
    setCallback?(cb: unknown): void;
    getDoneCallback?(): unknown;
    setDoneCallback?(cb: unknown): void;
    getDoneCallbackParams?(): unknown;
    setDoneCallbackParams?(params: unknown): void;
    [key: string]: unknown;
}

declare global {
    interface Window {
        CONFIGENTO_PRODUCT?: LegacyProduct;
        Justselling_Configento_App_Helper?: LegacyHelperShim;
        Justselling_Configento_App_CloudAsset?: LegacyCloudAssetShim;
        Justselling_Configento_App_Wizard?: LegacyWizardShim;
        Justselling_Configento_App_Webshop?: LegacyWebshopShim;
    }
}

const LEGACY_MOUNT_ID = 'configurator1';
const POLL_INTERVAL_MS = 50;
const POLL_TIMEOUT_MS = 30_000;

augmentLegacyMount();

waitForConfigento()
    .then((api) => api.ready.then(() => {
        wireInstances(api);
        installLegacyNamespaces(api);
    }))
    .catch((err) => {
        console.warn('[Configento compat] giving up:', err);
    });

/**
 * Step 1 — DOM. Runs synchronously before the V3 bundle scans for mount
 * targets. We only act if both conditions are true: legacy element
 * exists AND it's missing the new contract attribute. That way a host
 * page already using `[data-configento]` directly stays untouched.
 */
function augmentLegacyMount(): void {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(LEGACY_MOUNT_ID);
    if (!el || el.hasAttribute('data-configento')) return;

    el.setAttribute('data-configento', '');

    if (!el.dataset.componentId) {
        const tid = window.CONFIGENTO_PRODUCT?.getTemplateId?.();
        if (tid != null) el.dataset.componentId = String(tid);
    }
}

/**
 * Step 2 — wait for the V3 bundle to set `window.Configento`. The
 * polling timeout protects against missing-bundle scenarios (script tag
 * 404'd, CSP blocked, …) — at worst the compat layer logs a warn after
 * 30 s and stops; the page itself isn't affected.
 */
function waitForConfigento(): Promise<ConfiguratorPublicApi> {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('no window'));
    }
    if (window.Configento) return Promise.resolve(window.Configento);
    return new Promise((resolve, reject) => {
        const start = Date.now();
        const iv = setInterval(() => {
            if (window.Configento) {
                clearInterval(iv);
                resolve(window.Configento);
                return;
            }
            if (Date.now() - start > POLL_TIMEOUT_MS) {
                clearInterval(iv);
                reject(new Error('window.Configento never appeared'));
            }
        }, POLL_INTERVAL_MS);
    });
}

/**
 * Step 3 — once the bundle is ready, wire each instance's event stream
 * to the legacy event/callback surfaces.
 */
function wireInstances(api: ConfiguratorPublicApi): void {
    for (const inst of api.instances.values()) {
        wireOneInstance(inst);
    }
    // Legacy fired `…InDomAfter` once the template was first rendered;
    // themes restyle the initial markup there. `ready` resolves after the
    // first commit, so the configurator is on screen by now.
    if (api.instances.size > 0) scheduleLegacyAfter();
}

function wireOneInstance(inst: ConfiguratorInstance): void {
    // Legacy DOM-update events. Old theme code wired up `update*Before`
    // for "show a spinner" and `update*After` for "rehydrate sub-DOM".
    // V3 doesn't have a separate header-update vs options-update lifecycle
    // (everything is React-rendered atomically), so we fire BOTH pairs.
    // `Before` fires with the change, `After` once React has committed it,
    // see scheduleLegacyAfter. A step change re-renders the configurator
    // too, so it gets an `After` as well (`group` fires after its commit).
    inst.on('change', () => {
        fireLegacyEvent('configentoUpdateOptionsInDomBefore');
        fireLegacyEvent('configentoUpdateHeaderInDomBefore');
        scheduleLegacyAfter();
    });
    inst.on('group', () => scheduleLegacyAfter());

    // Legacy host-price callback. Old themes set up CONFIGENTO_PRODUCT
    // with their own `setPrice()` to mirror the total into a sticky bar
    // outside the configurator's own DOM. V3 has its own BottomBar; the
    // legacy hook is preserved so existing themes don't go silent.
    inst.on('price', ({ total }) => {
        try {
            window.CONFIGENTO_PRODUCT?.setPrice?.(total);
        } catch (err) {
            console.warn('[Configento compat] CONFIGENTO_PRODUCT.setPrice threw:', err);
        }
    });
}

function fireLegacyEvent(name: string): void {
    document.dispatchEvent(new CustomEvent(name));
}

let legacyAfterTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Fire the `…InDomAfter` pair once the DOM shows the change. `change` is
 * emitted from the store subscription, BEFORE React renders. Store updates
 * reach React through useSyncExternalStore, which renders synchronously
 * and commits (passive effects included) within the current task, so a
 * timer — the next task — always sees the committed DOM. Changes within
 * one task coalesce into a single pair, like one legacy re-render.
 */
function scheduleLegacyAfter(): void {
    if (legacyAfterTimer !== null) return;
    legacyAfterTimer = setTimeout(() => {
        legacyAfterTimer = null;
        fireLegacyEvent('configentoUpdateOptionsInDomAfter');
        fireLegacyEvent('configentoUpdateHeaderInDomAfter');
    }, 0);
}

/**
 * Step 4 — install legacy `Justselling_Configento_App_*` globals that the
 * saved-configuration / share-URL code in older storefronts reads from. We
 * use `??=` and per-method `??=` checks: if the host shop has already
 * defined any of these (custom Magento modules sometimes do), we don't
 * overwrite — the host code stays authoritative.
 */
function installLegacyNamespaces(api: ConfiguratorPublicApi): void {
    if (typeof window === 'undefined') return;

    window.Justselling_Configento_App_Helper ??= {};
    const helper = window.Justselling_Configento_App_Helper;
    helper.getConfigurator ??= () => api.first();
    helper.getTemplateId   ??= () => api.first()?.componentId ?? null;
    helper.getCustomerId   ??= () => api.first()?.config?.customer ?? null;
    helper.getBackendUrl   ??= () => api.first()?.config?.cloudBaseUrl ?? null;

    window.Justselling_Configento_App_CloudAsset ??= {};
    const cloudAsset = window.Justselling_Configento_App_CloudAsset;
    cloudAsset.storeCloudAsset ??= (_value, type, cb, filename) => {
        const inst = api.first();
        if (!inst) {
            // Legacy signature: callback fires without args on failure.
            try { cb?.(); } catch (err) { console.warn('[Configento compat] cb threw:', err); }
            return;
        }
        inst.storeAsset({ filename }).then(
            ({ id }) => {
                try {
                    // Legacy success signature: (assetsId, folder, type, file).
                    // V3 returns id+url only — folder/file are internal storage
                    // detail not surfaced via the new API. Callers depending on
                    // the folder/file fields will need to migrate to the new
                    // {id, url} shape.
                    cb?.(id, null, type ?? null, null);
                } catch (err) {
                    console.warn('[Configento compat] cb threw:', err);
                }
            },
            (err: unknown) => {
                console.warn('[Configento compat] storeCloudAsset failed:', err);
                try { cb?.(); } catch (e) { console.warn('[Configento compat] cb threw:', e); }
            },
        );
    };
    cloudAsset.getAssets ??= () => [];

    // ── Wizard shim ────────────────────────────────────────────────
    // Legacy AngularJS-era global. Magento's storefront product.js
    // hard-references `Justselling_Configento_App_Wizard.validateForm()`
    // in the AddToWishlist / AddToCart paths; without a shim the plain
    // JS crashes with a ReferenceError before any V3 logic even runs.
    // V3 validates on save-button submit inside the bundle, so the
    // shim returns permissive `true` here — the real gate lives one
    // layer deeper.
    window.Justselling_Configento_App_Wizard ??= {};
    const wizard = window.Justselling_Configento_App_Wizard;
    wizard.validateForm                  ??= () => true;
    wizard.checkValidationForActiveGroup ??= () => true;
    wizard.checkValidationForAllGroups   ??= () => true;
    // Legacy addressed steps by position; V3 navigates by group, so the
    // position is looked up among the groups currently shown. No validation:
    // legacy's setActivateGroup jumped unconditionally too.
    wizard.setActivateGroup              ??= (index: number) => {
        const inst = api.first();
        // A cached older bundle has no group navigation yet — stay a no-op.
        if (!inst || typeof inst.getGroups !== 'function') return;
        const target = inst.getGroups()[index];
        if (target) void inst.setActiveGroup(target.id, { validate: false });
    };
    wizard.setDisableGroup               ??= (_index: number) => { /* no-op */ };
    wizard.setEnableGroup                ??= (_index: number) => { /* no-op */ };
    wizard.updateWizard                  ??= () => { /* no-op */ };
    wizard.popUpMsg                      ??= () => { /* no-op */ };
    if (wizard.lastTabId === undefined) wizard.lastTabId = 0;
    if (wizard.init      === undefined) wizard.init      = false;

    // ── Webshop shim ───────────────────────────────────────────────
    // Legacy `finalizeAction(cb, params)` bundled together: rule
    // validation, cloud asset persistence, and the original Magento
    // AddToCart / AddToWishlist submit. V3's `inst.finalize()` returns
    // the same envelope in one call — we await it, then invoke the
    // legacy callback with the stored params so unchanged Magento
    // product.js keeps working. Storage of the header payload to the
    // cloud is deliberately NOT chained here: the shop's server-side
    // controller already reads the assets via other paths (Magento's
    // storefront POST intercept), and hosts that need the V3 share-
    // URL flow should call `inst.storeAsset()` explicitly instead of
    // relying on this shim.
    window.Justselling_Configento_App_Webshop ??= {
        callback: false,
        doneCallback: false,
        doneCallbackParams: [],
    };
    const webshop = window.Justselling_Configento_App_Webshop;
    webshop.finalizeAction ??= (doneCallback, doneParams) => {
        const inst = api.first();
        if (!inst) {
            // No configurator on this page — pass through to preserve
            // the "no-configurator product" AddToCart path in themes
            // that share product.js across all products.
            try { if (typeof doneCallback === 'function') doneCallback(doneParams); }
            catch (err) { console.warn('[Configento compat] doneCallback threw:', err); }
            return;
        }
        inst.finalize().then(
            (result) => {
                if (!result.ok) {
                    // Validation failed — legacy signalled via returning
                    // false; the caller (Magento's initAddToCart) then
                    // aborts submit. We can't return false async here,
                    // so we DON'T call the callback: matches "don't
                    // proceed with cart-add when config invalid".
                    return;
                }
                // Legacy contract: `params[0]` is the form element the
                // shop's next callback (originalAddToWishlist,
                // originalAddToCart) is about to submit. Magento's
                // Wishlist controller reads the buyRequest at
                // `options[<optionId>][configurator1-header]` — same
                // shape the AddToCart V3 branch already injects in
                // product.js. Inject here so the Wishlist submit path
                // (which lives purely in the legacy Webshop.finalizeAction
                // flow, no V3 branch of its own) picks up the current
                // configuration too.
                injectV3ConfigurationIntoForm(inst, doneParams);
                try { if (typeof doneCallback === 'function') doneCallback(doneParams); }
                catch (err) { console.warn('[Configento compat] doneCallback threw:', err); }
            },
            (err: unknown) => {
                console.warn('[Configento compat] finalizeAction failed:', err);
            },
        );
        // Return value is meaningless on the async path — legacy code
        // that read the return value from this function is broken by
        // design (the legacy version also queued jQuery.pleaseWait and
        // relied on the callback firing separately).
    };
    webshop.finalizeActionDone ??= () => { /* no-op — done-callback fires inline in finalizeAction */ };
    webshop.getCallback        ??= () => webshop.callback;
    webshop.setCallback        ??= (cb: unknown) => { webshop.callback = cb; };
    webshop.getDoneCallback    ??= () => webshop.doneCallback;
    webshop.setDoneCallback    ??= (cb: unknown) => { webshop.doneCallback = cb; };
    webshop.getDoneCallbackParams ??= () => webshop.doneCallbackParams;
    webshop.setDoneCallbackParams ??= (params: unknown) => { webshop.doneCallbackParams = params; };
}

/**
 * Inject V3 configuration hidden inputs into a form element, mirroring
 * the shape Magento's Configento module reads server-side. Used by the
 * `Webshop.finalizeAction` compat shim so the legacy AddToWishlist flow
 * (which still funnels through the shim on every V3 mount) hands
 * Magento the same field set the AddToCart V3 branch already injects
 * from `product.js`.
 *
 * Field shape (matches product.js:338-424 verbatim):
 *   options[<optionId>][configurator1-<altTitle>-<n>] = <value>   (per visible Char)
 *   options[<optionId>][configurator1-price]          = <total>   (bundle-calculated)
 *   options[<optionId>][configurator1-header]         = <base64>  (full snapshot)
 *
 * `optionId` comes from `mountEl.dataset.componentoOptionId` or the
 * legacy `CONFIGENTO_PRODUCT.getProductOptionId()` — the phtml template
 * sets both. Silently returns when neither is available or `params` is
 * not `[form]`.
 */
function injectV3ConfigurationIntoForm(
    inst: ConfiguratorInstance,
    params: unknown,
): void {
    const form = extractFormElement(params);
    if (!form) return;

    const headerValue = typeof inst.getHeaderValue === 'function' ? inst.getHeaderValue() : null;
    if (typeof headerValue !== 'string' || headerValue === '') return;

    const optionId = resolveOptionId(inst);
    if (optionId === null) return;

    // Per-Characteristic hidden inputs — decoded from the same base64
    // payload we're about to submit, so no extra bundle round-trip.
    // Skips visibility=0 (hidden) / visibility=3 (backend-only) per
    // legacy contract; skips underscore-prefixed metadata keys.
    let payload: Record<string, unknown> = {};
    try {
        payload = JSON.parse(atob(headerValue)) as Record<string, unknown>;
    } catch {
        payload = {};
    }
    let arrayIndex = 0;
    for (const altTitle of Object.keys(payload)) {
        if (altTitle.charAt(0) === '_') continue;
        const entry = payload[altTitle];
        if (!entry || typeof entry !== 'object') continue;
        const e = entry as { value?: unknown; visibility?: number };
        if (e.value === undefined || e.value === null) continue;
        if (e.visibility === 0 || e.visibility === 3) continue;
        arrayIndex++;
        const v = Array.isArray(e.value) ? e.value.join(',') : e.value;
        if (v === '' || v == null) continue;
        appendHidden(form, `options[${optionId}][configurator1-${altTitle}-${arrayIndex}]`, String(v));
    }

    // Bundle-calculated price → hidden field the server-side observer
    // reads to override Magento's product final-price (V3 stores prices
    // in the configurator state, not on option_type_value rows).
    if (typeof inst.getPriceBreakdown === 'function') {
        const price = inst.getPriceBreakdown();
        if (price && typeof price.total === 'number' && price.total > 0) {
            replaceHidden(form, `options[${optionId}][configurator1-price]`, String(price.total));
        }
    }

    // The wrapped Base64 header — full state snapshot, the field
    // Configento's Option-Type Model actually parses on the server.
    replaceHidden(form, `options[${optionId}][configurator1-header]`, headerValue);
}

/** `params` is the legacy `[form]` array, but tolerant of bare form
 *  references too. Returns the first HTMLFormElement / jQuery-wrapped
 *  form we can find, or null when the caller didn't hand us a form. */
function extractFormElement(params: unknown): HTMLFormElement | null {
    if (params instanceof HTMLFormElement) return params;
    if (Array.isArray(params) && params.length > 0) {
        const head = params[0];
        if (head instanceof HTMLFormElement) return head;
        // jQuery-wrapped element: `.get(0)` returns the raw HTMLFormElement.
        const jq = head as { get?(i: number): unknown };
        if (jq && typeof jq.get === 'function') {
            const raw = jq.get(0);
            if (raw instanceof HTMLFormElement) return raw;
        }
    }
    return null;
}

/** Where the phtml template writes the option id. First choice is the
 *  data attribute on the mount element (matches the pattern product.js
 *  uses for its AddToCart V3 branch). Falls back to the legacy global
 *  `CONFIGENTO_PRODUCT.getProductOptionId()` when the attribute is
 *  absent — some older plugin builds only set the global. */
function resolveOptionId(inst: ConfiguratorInstance): string | null {
    const mountEl = inst.element as HTMLElement | null;
    if (mountEl) {
        const fromDataset = mountEl.dataset.configentoOptionId;
        if (fromDataset) return fromDataset;
    }
    const legacyGlobal = window.CONFIGENTO_PRODUCT as { getProductOptionId?(): string | number | null } | undefined;
    if (legacyGlobal && typeof legacyGlobal.getProductOptionId === 'function') {
        const id = legacyGlobal.getProductOptionId();
        if (id !== null && id !== undefined && id !== '') return String(id);
    }
    return null;
}

function appendHidden(form: HTMLFormElement, name: string, value: string): void {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
}

/** Replace an existing hidden input with the same `name`, or append when
 *  none exists. Used for singleton fields (header, price) that would
 *  otherwise accumulate across repeat submits inside the same form. */
function replaceHidden(form: HTMLFormElement, name: string, value: string): void {
    // querySelector with attribute name containing `[` needs escaping;
    // use CSS.escape when available, else old-fashioned NodeList scan.
    const existing = form.querySelector(`input[name="${cssEscape(name)}"]`);
    if (existing instanceof HTMLInputElement) {
        existing.value = value;
        return;
    }
    appendHidden(form, name, value);
}

function cssEscape(s: string): string {
    // Modern browsers all ship CSS.escape; the fallback covers only
    // the characters we actually use in these field names.
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(s);
    }
    return s.replace(/[\[\]"\\]/g, '\\$&');
}
